"use client";

import { Spinner } from "@/components/ui/spinner";

import { useEffect, useState } from "react";
import { useAppRouter as useRouter } from "@/lib/navigation";
import { AlertTriangle, CheckCircle2, Clock3, ListTodo, Lock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CamundaTaskService, type TaskDeepLinkResolveResult } from "@/lib/camunda/task-service";

const MAX_PENDING_ATTEMPTS = 6;
const PENDING_RETRY_MS = 1500;

type ResolverViewState =
  | TaskDeepLinkResolveResult["status"]
  | "resolving"
  | "redirecting";

type TaskOpenClientProps = {
  processInstanceId: string;
  taskDefinitionKey: string;
  sentAt: string;
};

const viewText = {
  resolving: {
    title: "Buscando la tarea",
    description: "Estamos comprobando el enlace con Camunda.",
  },
  redirecting: {
    title: "Abriendo la tarea",
    description: "La tarea esta activa y disponible para tu usuario.",
  },
  active: {
    title: "Abriendo la tarea",
    description: "La tarea esta activa y disponible para tu usuario.",
  },
  pending: {
    title: "La tarea todavia no esta lista",
    description: "El correo puede haberse enviado justo antes de crear la tarea. Reintenta en unos segundos.",
  },
  completed_or_ended: {
    title: "Este enlace ya no tiene una tarea activa",
    description: "Puede que la tarea se haya completado, reemplazado o que el proceso haya avanzado.",
  },
  forbidden: {
    title: "No tienes permiso para abrir esta tarea",
    description: "Camunda no ha devuelto una tarea disponible para tu usuario.",
  },
  ambiguous: {
    title: "Hay mas de una tarea posible",
    description: "Abre tus tareas y elige la tarea correspondiente desde la lista.",
  },
  invalid_link: {
    title: "El enlace no es valido",
    description: "Faltan datos del proceso o de la tarea en el enlace del correo.",
  },
  error: {
    title: "No se pudo comprobar el enlace",
    description: "Hubo un problema consultando Camunda. Puedes reintentar o volver a tus tareas.",
  },
} satisfies Record<ResolverViewState, { title: string; description: string }>;

function StatusIcon({ state }: { state: ResolverViewState }) {
  if (state === "resolving" || state === "redirecting" || state === "active") {
    return <Spinner size={24} />;
  }

  if (state === "pending") {
    return <Clock3 className="size-6" />;
  }

  if (state === "forbidden") {
    return <Lock className="size-6" />;
  }

  if (state === "completed_or_ended") {
    return <CheckCircle2 className="size-6" />;
  }

  return <AlertTriangle className="size-6" />;
}

function getStatusClasses(state: ResolverViewState): string {
  if (state === "forbidden" || state === "invalid_link" || state === "error") {
    return "border-red-200 bg-red-50 text-red-700";
  }

  if (state === "pending" || state === "ambiguous") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }

  return "border-blue-200 bg-blue-50 text-blue-700";
}

export default function OpenTaskClient({
  processInstanceId,
  taskDefinitionKey,
  sentAt,
}: TaskOpenClientProps) {
  const router = useRouter();
  const [viewState, setViewState] = useState<ResolverViewState>("resolving");
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    async function resolveLink(attempt: number) {
      if (!processInstanceId || !taskDefinitionKey) {
        setViewState("invalid_link");
        return;
      }

      setViewState("resolving");

      let result: TaskDeepLinkResolveResult;
      try {
        result = await CamundaTaskService.resolveTaskDeepLink({
          processInstanceId,
          taskDefinitionKey,
          sentAt: sentAt || null,
        });
      } catch {
        if (!cancelled) {
          setViewState("error");
        }
        return;
      }

      if (cancelled) {
        return;
      }

      if (result.status === "active") {
        setViewState("redirecting");
        router.replace(`/task-list/tasks/${encodeURIComponent(result.task.id)}`);
        return;
      }

      if (result.status === "pending" && attempt < MAX_PENDING_ATTEMPTS) {
        retryTimer = setTimeout(() => {
          void resolveLink(attempt + 1);
        }, PENDING_RETRY_MS);
        return;
      }

      setViewState(result.status);
    }

    void resolveLink(1);

    return () => {
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [processInstanceId, taskDefinitionKey, sentAt, retryKey, router]);

  const text = viewText[viewState];
  const canRetry = viewState !== "resolving" && viewState !== "redirecting" && viewState !== "active";

  return (
    <div className="flex h-full min-h-screen items-center justify-center bg-white px-6">
      <div className="w-full max-w-md text-center">
        <div className={`mx-auto mb-5 flex size-14 items-center justify-center rounded-full border ${getStatusClasses(viewState)}`}>
          <StatusIcon state={viewState} />
        </div>
        <h1 className="text-2xl font-semibold tracking-normal text-gray-950">{text.title}</h1>
        <p className="mt-3 text-sm leading-6 text-gray-600">{text.description}</p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Button type="button" onClick={() => router.replace("/task-list/tasks")}>
            <ListTodo className="size-4" />
            Ir a mis tareas
          </Button>
          {canRetry && (
            <Button type="button" variant="outline" onClick={() => setRetryKey((current) => current + 1)}>
              <RefreshCw className="size-4" />
              Reintentar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
